import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { CameraRest } from '@hunting-nature/shared-core/interfaces/camera-rest.interface';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';
import { ResourceAvailabilityValues } from '@hunting-nature/shared-core/types/resource-availability.type';
import { SubscriptionPlanValues } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { map, of, type Observable } from 'rxjs';

import { convertToCamera, getCameraIdentCodeByCameraDetails } from '../../../../shared/helper/camera.helper';
import { RestService } from '../../../../shared/services/rest-service';
import { NoRequestsLeftVariantValues, VideoModalTypeValues, VideoRequestOutcomeValues } from '../video-request.types';
import type { NoRequestsLeftVariant, VideoRequestEvaluation } from '../video-request.types';

import { AppConfigLoader } from 'apps/Secacam/src/app/config/app-config.loader';

@Injectable({
  providedIn: 'root',
})
export class VideoRequestService {
  // services
  private readonly httpClient = inject(HttpClient);
  private readonly restService = inject(RestService);

  private readonly appConfigLoader = inject(AppConfigLoader);

  private get backend() {
    return this.appConfigLoader.activeConfig().backend;
  }

  findCameraById$(cameraId: string): Observable<Camera | undefined> {
    return this.restService.getCamerasUserCanAccess$().pipe(
      map(response => {
        const cameraRest = response.cameras.find((c: CameraRest) => c.id === cameraId);
        return cameraRest ? convertToCamera(undefined, cameraRest) : undefined;
      })
    );
  }

  requestVideo$(cameraIdentCode: string, imageId: string): Observable<void> {
    const url = `${this.backend.remoteControlUpdateApiUrl}/rc-jobs/on-demand-video/camera-ident-codes/${cameraIdentCode}/imageId/${imageId}`;

    return this.httpClient.post(url, undefined, { responseType: 'text' }).pipe(map(() => {}));
  }

  evaluateRequestAction(camera: Camera, isOffline: boolean): VideoRequestEvaluation {
    const cameraIdentCode = getCameraIdentCodeByCameraDetails(camera);

    if (isOffline) {
      return { outcome: VideoRequestOutcomeValues.BLOCKED_OFFLINE };
    }

    if (camera.permission === PermissionValues.VIEWER) {
      return { outcome: VideoRequestOutcomeValues.BLOCKED_NO_PERMISSION };
    }

    if (camera.permission === PermissionValues.OPERATOR) {
      return {
        outcome: VideoRequestOutcomeValues.SHOW_MODAL_OPERATOR,
        modal: VideoModalTypeValues.OPERATOR,
        cameraIdentCode,
      };
    }

    return this.evaluateOwnerRequest(camera, cameraIdentCode);
  }

  resolveVideoRequest(camera: Camera | undefined, isOffline: boolean): VideoRequestEvaluation {
    if (!camera) {
      return { outcome: VideoRequestOutcomeValues.BLOCKED_NO_CAMERA };
    }
    return this.evaluateRequestAction(camera, isOffline);
  }

  evaluateVideoRequest$(camera: Camera | undefined, cameraId: string, isOffline: boolean): Observable<VideoRequestEvaluation> {
    const camera$ = camera ? of(camera) : this.findCameraById$(cameraId);
    return camera$.pipe(map(cam => this.resolveVideoRequest(cam, isOffline)));
  }

  private evaluateOwnerRequest(camera: Camera, cameraIdentCode: string | undefined): VideoRequestEvaluation {
    const availability = camera.availableVideos;

    switch (availability) {
      case ResourceAvailabilityValues.OK:
      case ResourceAvailabilityValues.LOW:
        return {
          outcome: VideoRequestOutcomeValues.REQUEST,
          cameraIdentCode,
        };

      case ResourceAvailabilityValues.NONE:
        return {
          outcome: VideoRequestOutcomeValues.SHOW_MODAL_NO_REQUESTS,
          modal: VideoModalTypeValues.NO_REQUESTS_LEFT,
          noRequestsLeftVariant: this.resolveNoRequestsLeftVariant(camera),
          cameraIdentCode,
        };
    }

    return {
      outcome: VideoRequestOutcomeValues.REQUEST,
      cameraIdentCode,
    };
  }

  private resolveNoRequestsLeftVariant(camera: Camera): NoRequestsLeftVariant {
    const plan = camera.subscriptionPlan;

    if (plan === SubscriptionPlanValues.PROFESSIONAL) {
      return NoRequestsLeftVariantValues.PROFESSIONAL;
    }

    if (plan === SubscriptionPlanValues.PREMIUM) {
      return NoRequestsLeftVariantValues.PREMIUM;
    }

    return NoRequestsLeftVariantValues.PREPAID;
  }
}
